import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { getSpecificDetails, updateData } from '../../service/ApiService';
import { UPDATE_EMPLOYEE_DOCUMENTS, GET_TAX_FORM_DOCUMENTS } from '../../config/ApiConstant';
import FileUploader from '../../components/FileUploader/FileUploader';
import {
  FileText,
  CheckCircle,
  User,
  CheckCircle2,
  HeartHandshake
} from 'lucide-react';
import { baseDocTypes } from '../../appconfig/Settings';


const UpdateEmployeeDocuments: React.FC = () => {
  const toast = useRef<any>(null);
  const { taxformId }: any = useParams();
  const [realTaxformId, setRealTaxformId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>(null);
  const [taxPayerDocuments, setTaxPayerDocuments] = useState<any[]>([]);
  const [spouseDocuments, setSpouseDocuments] = useState<any[]>([]);
  const [pendingFiles, setPendingFiles] = useState<{ [key: string]: any }>({});
  const [submitted, setSubmitted] = useState<boolean>(false);

  useEffect(() => {
    try {
      if (taxformId) {
        setRealTaxformId(atob(taxformId));
      }
    } catch (e) {
      console.error('Invalid base64 ID', e);
      setRealTaxformId(null);
    }
  }, [taxformId]);

  useEffect(() => {
    if (realTaxformId) {
      fetchTaxpayerDetails();
    } else if (taxformId === undefined) {
      setLoading(false);
    }

    document.title = 'Nagra Accounting | Update Documents';
  }, [realTaxformId, taxformId]);

  const isMarried =
    userData?.is_spouse_canadian_resident == true;

  const fetchTaxpayerDetails = async () => {
    if (!realTaxformId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await getSpecificDetails(
        GET_TAX_FORM_DOCUMENTS,
        realTaxformId
      );

      const docs = response;

      setTaxPayerDocuments(docs?.tax_payer_documents || []);
      setSpouseDocuments(docs?.spouse_documents || []);

      if (response?.user) {
        setUserData(response.user);
      } else if (docs) {
        setUserData(docs);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail:
          error.response?.data?.message || 'Failed to fetch details'
      });
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (e: any, docType: string) => {
    setPendingFiles((prev) => ({
      ...prev,
      [docType]: e.files
    }));
  };

  const handleUpload = async () => {
    const allFiles = { ...pendingFiles };
    const hasFiles = Object.keys(allFiles).length > 0;

    if (!hasFiles) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Required',
        detail: 'Please select at least one file to upload'
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();

      Object.keys(allFiles).forEach((docType) => {
        const files = allFiles[docType];

        if (files) {
          if (Array.isArray(files)) {
            files.forEach((file: File) => {
              formData.append(docType, file);
            });
          } else {
            formData.append(docType, files);
          }
        }
      });

      const response = await updateData(
        UPDATE_EMPLOYEE_DOCUMENTS,
        realTaxformId,
        formData
      );

      if (response) {
        setPendingFiles({});
        fetchTaxpayerDetails();
        setSubmitted(true);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Update Failed',
        detail:
          error.response?.data?.message ||
          'Failed to update documents'
      });
    } finally {
      setUploading(false);
    }
  };

  const viewPdf = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // TAX PAYER: search taxPayerDocuments only
  const getTaxPayerDocsByType = (
    docTypeValue: string,
    allDocs: any[]
  ) => {
    if (!allDocs || !Array.isArray(allDocs)) return [];

    const cleanType = docTypeValue
      .replace(/_file$/i, '')
      .toUpperCase();

    return allDocs.filter((doc) => {
      if (!doc.type) return false;
      const docType = doc.type.toUpperCase();
      if (docType.startsWith('SPOUSE ') || docType.startsWith('SPOUSE_')) return false;
      return docType === cleanType || docType === `${cleanType}_FILE`;
    });
  };

  // SPOUSE: search spouseDocuments only
  const getSpouseDocsByType = (
    docTypeValue: string,
    allDocs: any[]
  ) => {
    if (!allDocs || !Array.isArray(allDocs)) return [];

    const cleanType = docTypeValue
      .replace(/^spouse_/i, '')
      .replace(/_file$/i, '')
      .toUpperCase();

    return allDocs.filter((doc) => {
      if (!doc.type) return false;
      const docType = doc.type.toUpperCase();
      return (
        docType === cleanType ||                   // plain: "T4"
        docType === `${cleanType}_FILE` ||         // "T4_FILE"
        docType === `SPOUSE ${cleanType}` ||       // "SPOUSE T4"  ← actual API format (space!)
        docType === `SPOUSE_${cleanType}` ||       // "SPOUSE_T4"  (underscore fallback)
        docType === `SPOUSE ${cleanType}_FILE` ||  // "SPOUSE T4_FILE"
        docType === `SPOUSE_${cleanType}_FILE`     // "SPOUSE_T4_FILE"
      );
    });
  };

  const DocumentUploadSection = ({
    docList,
    docsState,
    title,
    icon,
    isSpouse = false,
    showSizeLimit = true
  }: any) => {
    return (
      <div className="document-section">
        {(title || icon) && (
          <div className="section-header">
            {icon && <div className="section-icon">{icon}</div>}
            {title && <h2>{title}</h2>}
          </div>
        )}

        <div className="grid p-fluid">
          {docList.map((doc: any) => {
            const existing = isSpouse
              ? getSpouseDocsByType(doc.value, docsState)
              : getTaxPayerDocsByType(doc.value, docsState);

            return (
              <div
                key={doc.value}
                className="field col-12 md:col-4 p-3"
              >
                <label className="document-label">
                  {doc.label}
                  {existing.length > 0 && (
                    <i className="pi pi-check-circle text-green-500 ml-2"></i>
                  )}
                </label>

                <div className="uploader-box horizontal-uploader">
                  <FileUploader
                    name={doc.value}
                    onInputChange={(e) =>
                      onFileChange(e, doc.value)
                    }
                    multiple={true}
                    maxSize={10}
                    value={pendingFiles[doc.value]}
                    showSizeLimit={showSizeLimit}
                  />
                </div>

                {existing.length > 0 && (
                  <div className="uploaded-box">
                    <div className="uploaded-header">
                      <CheckCircle
                        size={14}
                        className="text-green-600"
                      />
                      <span>Previously Uploaded:</span>
                    </div>

                    {existing.map((file: any, i: number) => (
                      <div
                        key={file.id || i}
                        className="uploaded-file"
                      >
                        <span
                          className="file-name"
                          title={file.file_url?.split('/').pop()}
                        >
                          {file.file_url?.split('/').pop() || `Document ${i + 1}`}
                        </span>

                        <Button
                          icon="pi pi-eye"
                          className="p-button-text p-button-sm"
                          onClick={() => viewPdf(file.file_url)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (submitted) {
    return (
      <div className="update-documents-page">
        <Toast ref={toast} />
        <div className="topbar">
          <div className="topbar-content">
            <div className="logo-container">
              <img
                src="/assets/images/Nexawage_new_logo_white.png"
                alt="logo"
                className="logo-image"
              />
              <span className="logo-text">Nagra Accounting</span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              maxWidth: '500px',
              width: '100%',
              background: '#ffffff',
              borderRadius: '24px',
              padding: '50px 35px',
              textAlign: 'center',
              border: '1px solid #e5eefc'
            }}
          >
            <div
              style={{
                width: '95px',
                height: '95px',
                margin: '0 auto 20px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2563eb, #60a5fa)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#fff',
                boxShadow: '0 8px 25px rgba(37,99,235,0.3)'
              }}
            >
              <CheckCircle2 size={52} strokeWidth={1.5} />
            </div>

            <h1
              style={{
                fontSize: '2.3rem',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '12px'
              }}
            >
              Thank You!
            </h1>

            <p
              style={{
                fontSize: '1.1rem',
                color: '#4b5563',
                marginBottom: '14px',
                lineHeight: '1.7'
              }}
            >
              Your documents have been submitted successfully.
            </p>

            <p
              style={{
                fontSize: '0.98rem',
                color: '#6b7280',
                lineHeight: '1.8',
                marginBottom: '30px'
              }}
            >
              Our team at <strong>Nagra Accounting</strong> will review your
              documents and get back to you shortly.
            </p>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="update-documents-page">
      <Toast ref={toast} />

      <div className="topbar">
        <div className="topbar-content">
          <div className="logo-container">
            <img
              src="/assets/images/Nexawage_new_logo_white.png"
              alt="logo"
              className="logo-image"
            />
            <span className="logo-text">Nagra Accounting</span>
          </div>


        </div>
      </div>

      <div className="main-content">
        <div className="page-header">
          <h1>Update Your Documents</h1>
          <p>Review and upload your tax documentation</p>
        </div>

        <TabView className="main-tabview shadow-2 border-round-xl overflow-hidden bg-white mt-4">
          <TabPanel
            header="Tax Payer Document"
            leftIcon="pi pi-user mr-2"
          >
            <div style={{ color: "rgb(255, 0, 0)" }} className="mb-3">
              Note: Please upload files less than 8MB
            </div>
            <DocumentUploadSection
              docList={baseDocTypes}
              docsState={taxPayerDocuments}
              showSizeLimit={false}
            />
          </TabPanel>

          {isMarried && (
            <TabPanel
              header="Spouse Documents"
              leftIcon="pi pi-users mr-2"
            >
              <div style={{ color: "rgb(255, 0, 0)" }} className="mb-3">
                Note: Please upload files less than 8MB
              </div>
              <DocumentUploadSection
                docList={baseDocTypes.map((d) => ({
                  ...d,
                  label: `Spouse ${d.label}`,
                  value: `spouse_${d.value}`
                }))}
                docsState={spouseDocuments}
                isSpouse={true}
                showSizeLimit={false}
              />
            </TabPanel>
          )}
        </TabView>

        {/* Single unified upload button — right aligned, outside TabView */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            marginTop: '1rem'
          }}
        >
          <Button
            label={uploading ? 'Uploading...' : 'Upload Documents'}
            icon={
              uploading
                ? 'pi pi-spin pi-spinner'
                : 'pi pi-cloud-upload'
            }
            onClick={handleUpload}
            style={{ backgroundColor: "#2563eb" }}
            disabled={uploading || Object.keys(pendingFiles).length === 0}
          />
        </div>
      </div>
    </div>
  );
};

export default UpdateEmployeeDocuments;